import PaginatedSelect from "@/components/ui/PaginatedSelect";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptLabels } from "@/queries/prompt-labels";
import { UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export type ReleaseLabel = {
  id: number;
  name: string;
};

const ReleaseLabelSelector = ({
  selectedLabel,
  onLabelSelect,
  onLabelRemove,
  showLabel = true,
}: {
  selectedLabel: ReleaseLabel | undefined;
  onLabelSelect: (label: ReleaseLabel) => void;
  onLabelRemove: (id: number) => void;
  showLabel?: boolean;
}) => {
  const userToken = useUserToken();
  const workspaceId = useUser()?.activeWorkspaceId!;
  const { promptId } = useParams();

  const Query = (
    page: number,
  ): UseQueryResult<
    { labels: ReleaseLabel[]; total: number; pages: number },
    unknown
  > | null => {
    const result = usePromptLabels(
      userToken,
      workspaceId,
      promptId ? Number(promptId) : undefined,
      page,
    );

    if (!result.data)
      return result as unknown as UseQueryResult<
        { labels: ReleaseLabel[]; total: number; pages: number },
        unknown
      > | null;

    return {
      ...result,
      data: {
        labels: result.data.prompt_labels.map((label) => ({
          id: label.id,
          name: label.name,
        })),
        total: result.data.total,
        pages: result.data.pages,
      },
    } as UseQueryResult<
      { labels: ReleaseLabel[]; total: number; pages: number },
      unknown
    > | null;
  };

  return (
    <div>
      {showLabel && (
        <label
          htmlFor="release-label"
          className="mb-1 block text-sm font-medium"
        >
          Release Label
        </label>
      )}

      <PaginatedSelect<
        ReleaseLabel,
        { labels: ReleaseLabel[]; total: number; pages: number }
      >
        query={Query}
        renderItem={(label) => label.name}
        getItemValue={(label) => label.id.toString()}
        extractData={(data) => ({
          data: data.labels,
          totalPages: data.pages,
        })}
        placeholder="Select release label"
        value={
          selectedLabel
            ? {
                id: selectedLabel.id.toString(),
                item: {
                  id: selectedLabel.id.toString(),
                  name: selectedLabel.name,
                },
              }
            : undefined
        }
        onRemove={() => onLabelRemove(selectedLabel?.id!)}
        onValueChange={(value) => {
          onLabelSelect(value.item);
        }}
      />
    </div>
  );
};

export default ReleaseLabelSelector;
